import { motion } from "framer-motion";
import { Plane, Train, Car, MapPin, Navigation } from "lucide-react";
import { SectionHeading } from "./Story";

const iconFor = (mode) => {
  const m = (mode || "").toLowerCase();
  if (m.includes("air") || m.includes("flight")) return Plane;
  if (m.includes("train") || m.includes("rail")) return Train;
  return Car;
};

export const Travel = ({ travel, venue }) => (
  <section id="travel" className="py-20 sm:py-28 lg:py-32 bg-[var(--ivory)]" data-testid="travel-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16">
      <SectionHeading script="plan your trip" title={travel?.title || "Getting There"} testid="travel-heading" />

      {/* Venue map & directions */}
      {venue && (
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 grid lg:grid-cols-2 gap-6 items-stretch bg-[var(--card)] rounded-2xl border border-[var(--border)] overflow-hidden"
          data-testid="venue-map-card"
        >
          <div className="relative w-full h-64 sm:h-80 lg:h-full lg:min-h-[360px] bg-[var(--surface)]">
            {venue.map_embed ? (
              <iframe
                title="Venue location"
                src={venue.map_embed}
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="venue-map-iframe"
              />
            ) : (
              <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-[var(--text-muted)] gap-2 p-6 text-center">
                <MapPin className="w-8 h-8 text-[var(--gold)]" />
                <p className="text-sm">{venue.directions_note}</p>
              </div>
            )}
          </div>
          <div className="p-8 flex flex-col justify-center">
            <p className="font-script text-2xl text-[var(--gold)]">the venue</p>
            <h3 className="font-serif text-3xl text-[var(--burgundy)] mt-1" data-testid="venue-name">{venue.name}</h3>
            <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[var(--gold)] mt-0.5 shrink-0" /> {venue.address}
            </p>
            {venue.map_link && (
              <a
                href={venue.map_link}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white w-fit transition-all hover:scale-105"
                style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }}
                data-testid="venue-directions-btn"
              >
                <Navigation className="w-4 h-4" /> Get Directions
              </a>
            )}
          </div>
        </motion.div>
      )}

      {travel?.description && (
        <p className="text-center text-[var(--text-secondary)] text-sm sm:text-base mb-8 leading-relaxed max-w-2xl mx-auto">
          {travel.description}
        </p>
      )}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="travel-block">
        {(travel?.items || []).map((it, i) => {
          const Icon = iconFor(it.mode);
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[var(--card)] rounded-xl p-6 border border-[var(--border)] text-center"
              data-testid={`travel-item-${i}`}
            >
              <div className="rounded-full p-3 w-fit mx-auto mb-3" style={{ backgroundColor: "var(--rose)" }}>
                <Icon className="w-5 h-5 text-[var(--burgundy)]" />
              </div>
              <p className="font-medium text-[var(--text-primary)]">{it.mode}</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1 leading-relaxed">{it.detail}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
